import { useIsReadOnly } from '@/lib/casl'
import { truncateFieldValueToCharacterLimit } from '@/lib/field-character-limits'
import { useFieldContext } from '@/lib/form-contexts'
import { cn } from '@/lib/utils'
import { forwardRef, type ReactNode, useId } from 'react'
import { InputGroup, type InputGroupProps } from '../ui/input-group'
import { Label } from '../ui/label'
import { Typography } from '@/components/ui/typography'
import FieldInfo from './FieldInfo'

export type TextInputProps = InputGroupProps & {
  label?: ReactNode
  labelEnd?: ReactNode
  labelClassName?: string
  inputEnd?: ReactNode
  readOnlyEmptyText?: ReactNode
  transformValue?: ((value: string) => string) | null
  characterMaxLength?: number | null
}

interface TextInputControlProps extends TextInputProps {
  fieldName: string
  fieldValue: string | number | null | undefined
  onFieldChange: (value: string) => void
  onFieldBlur?: () => void
  fieldInfo?: ReactNode
}

export const TextInputControl = forwardRef<HTMLInputElement, TextInputControlProps>(
  function TextInputControl(
    {
      fieldName,
      fieldValue,
      onFieldChange,
      onFieldBlur = () => {},
      fieldInfo,
      id,
      label = null,
      type = 'text',
      labelEnd = '',
      labelClassName = '',
      placeholder = '',
      className = '',
      inputClassName = '',
      inputEnd = null,
      readOnlyEmptyText,
      transformValue = null,
      characterMaxLength = null,
      onChange,
      onBlur,
      ...props
    },
    ref
  ) {
    const { isReadOnly } = useIsReadOnly()
    const randomId = useId()
    const inputId = id || `${randomId}-${fieldName}`

    const displayValue = fieldValue || (isReadOnly ? readOnlyEmptyText : '')

    return (
      <div className={cn('flex flex-col gap-y-2 w-full', className)}>
        {!!label && (
          <div className="inline-flex items-center">
            <Label htmlFor={inputId} className={labelClassName}>
              {label}
            </Label>
            {labelEnd}
          </div>
        )}

        {isReadOnly ? (
          <Typography variant="label" tone={!fieldValue ? 'muted' : 'default'}>
            {displayValue}
          </Typography>
        ) : (
          <InputGroup
            inputRef={ref}
            id={inputId}
            type={type}
            placeholder={placeholder}
            value={fieldValue ?? ''}
            onChange={(e) => {
              const transformedValue =
                typeof transformValue === 'function'
                  ? transformValue(e.target.value)
                  : e.target.value
              const nextValue = truncateFieldValueToCharacterLimit(
                transformedValue,
                characterMaxLength
              )

              onFieldChange(nextValue)
              onChange?.(e)
            }}
            onBlur={(e) => {
              onFieldBlur()
              onBlur?.(e)
            }}
            endAdornment={inputEnd}
            inputClassName={cn('w-full', inputClassName)}
            {...props}
          />
        )}
        {type !== 'hidden' && fieldInfo}
      </div>
    )
  }
)

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(props, ref) {
  const field = useFieldContext<string | number | null | undefined>()
  return (
    <TextInputControl
      {...props}
      ref={ref}
      fieldName={field.name}
      fieldValue={field.state.value}
      onFieldChange={field.handleChange}
      onFieldBlur={field.handleBlur}
      fieldInfo={<FieldInfo />}
    />
  )
})

export default TextInput
